import { useSearchParams } from "react-router"
import {
  useInquiryFullDetail,
  useInquiryMatchCandidates,
  type SupplierInquiryListItem,
} from "../../../api/supplierInquiriesApi"
import { EntityEmptyDetail } from "../../shared/EntityEmptyDetail"
import { EntityListSkeleton } from "../../shared/EntityListSkeleton"
import { InquiryBulkActionsPanel } from "./InquiryBulkActionsPanel"
import { InquiryDetailHeader } from "./InquiryDetailHeader"
import { InquiryNotesSection } from "./InquiryNotesSection"
import { InquiryRecipientsSection } from "./InquiryRecipientsSection"
import { InquiryScopeSection } from "./InquiryScopeSection"
import { InquirySupplierWarningBanner } from "./InquirySupplierWarningBanner"
import { InquiryTimelineSection } from "./InquiryTimelineSection"
import { MatchCandidatePanel } from "./MatchCandidatePanel"

interface InquiryDetailPanelProps {
  selectedRows: SupplierInquiryListItem[]
  onRemoveIds: (ids: string[]) => void
  onClearSelection: () => void
}

export function InquiryDetailPanel({
  selectedRows,
  onRemoveIds,
  onClearSelection,
}: InquiryDetailPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const id = searchParams.get("id")
  // On the matching tab `?id=` names a match candidate, not an inquiry — so the inquiry
  // fetch stays disabled there and the pane reviews the email instead.
  const isMatchingView = searchParams.get("status") === "matching"
  const { data: inquiry, isLoading } = useInquiryFullDetail(
    isMatchingView ? null : id,
  )
  const { data: matchesPage } = useInquiryMatchCandidates(isMatchingView)

  if (isMatchingView) {
    return (
      // Keyed on the candidate: selecting a different email must drop whatever inquiry was
      // staged or being previewed for the previous one.
      <MatchCandidatePanel
        key={id ?? "none"}
        candidate={matchesPage?.pages
          .flatMap((page) => page.items)
          .find((candidate) => candidate.candidateId === id)}
      />
    )
  }

  // A live multi-selection takes over the detail pane with bulk actions (mirrors the
  // Master Data supplier pattern). Placed after all hooks so rules-of-hooks hold.
  if (selectedRows.length > 0) {
    return (
      <InquiryBulkActionsPanel
        selectedRows={selectedRows}
        onRemoveIds={onRemoveIds}
        onClearSelection={onClearSelection}
      />
    )
  }
  if (!id) return <EntityEmptyDetail body="MC_MD_EMPTY_SELECT_BODY_INQUIRY" />
  if (isLoading) {
    return (
      <div className="p-4">
        <EntityListSkeleton />
      </div>
    )
  }
  if (!inquiry) return <EntityEmptyDetail body="MC_MD_EMPTY_SELECT_BODY_INQUIRY" />

  const clearSelection = () => {
    const next = new URLSearchParams(searchParams)
    next.delete("id")
    setSearchParams(next)
  }

  return (
    // absolute inset-0 (as SendInquiryPanel does): sizes against the ScrollArea's
    // positioned root, not its content-sized `display: table` wrapper, so the header
    // below has a real width to wrap against. Trade-off: the body scrolls itself.
    <div className="absolute inset-0 flex flex-col">
      <InquiryDetailHeader inquiry={inquiry} onDiscarded={clearSelection} />
      <div className="flex-1 overflow-y-auto">
        <InquirySupplierWarningBanner
          supplierName={inquiry.supplierName}
          warnings={inquiry.supplierWarnings}
        />
        <InquiryScopeSection inquiry={inquiry} />
        <InquiryRecipientsSection
          recipientsTo={inquiry.recipientsTo}
          recipientsCc={inquiry.recipientsCc}
        />
        {/* Keyed so a draft typed on one inquiry is not carried to the next. */}
        <InquiryNotesSection key={inquiry.id} inquiryId={inquiry.id} />
        <InquiryTimelineSection inquiry={inquiry} />
      </div>
    </div>
  )
}
